<template>
  <v-dialog v-model="dialog" max-width="800px">
    <v-card>
      <!-- 标题区块 -->
      <v-card-title class="d-flex flex-column pa-4">
        <!-- 主题信息区块 -->
        <div class="d-flex align-start w-100 mb-4">
          <!-- 左侧：封面图 -->
          <div class="topic-cover mr-4" style="width: 200px; height: 200px;">
            <v-img
              :src="formData.coverImageUrl"
              height="100%"
              width="100%"
              class="rounded-lg"
            >
              <!-- 遮罩层 -->
              <div 
                class="cover-overlay d-flex align-center justify-center"
                @click="triggerImageUpload"
              >
                <span class="white--text">更换封面图</span>
              </div>
            </v-img>
            <!-- 隐藏的文件上传输入框 -->
            <input
              ref="imageInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleImageChange"
            >
          </div>

          <!-- 右侧：主题信息 -->
          <div class="flex-grow-1">
            <!-- 主题名称 -->
            <div class="d-flex align-center mb-2">
              <template v-if="!isEditingName">
                <span class="text-h5">{{ formData.topicName }}</span>
                <v-btn icon small class="ml-2" @click="startEditingName">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <div v-else class="d-flex align-center flex-grow-1">
                <v-text-field
                  v-model="editingTopicName"
                  :rules="[
                    v => !!v || '课程主题名称不能为空',
                    v => v.length <= 50 || '课程主题名称不能超过50个字符'
                  ]"
                  counter="50"
                  hide-details="auto"
                  class="flex-grow-1 mr-2"
                  @keyup.esc="cancelEditingName"
                ></v-text-field>
                <v-btn small color="primary" @click="saveTopicName">保存</v-btn>
                <v-btn small text @click="cancelEditingName" class="ml-2">取消</v-btn>
              </div>
            </div>

            <!-- 主题描述 -->
            <div class="mb-2">
              <template v-if="!isEditingDescription">
                <div class="d-flex align-start">
                  <span class="grey--text text--darken-1">
                    {{ formData.description || '暂无描述' }}
                  </span>
                  <v-btn icon small class="ml-2" @click="startEditingDescription">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
              <div v-else>
                <v-textarea
                  v-model="editingDescription"
                  rows="3"
                  counter="200"
                  :rules="[v => v.length <= 200 || '描述不能超过200个字符']"
                  placeholder="请输入主题描述"
                  hide-details="auto"
                  class="mb-2"
                  @keyup.esc="cancelEditingDescription"
                ></v-textarea>
                <div class="d-flex justify-end">
                  <v-btn small color="primary" @click="saveDescription">保存</v-btn>
                  <v-btn small text @click="cancelEditingDescription" class="ml-2">取消</v-btn>
                </div>
              </div>
            </div>

            <!-- 更新按钮 -->
            <div class="d-flex justify-end">
              <v-btn
                small
                color="primary"
                :disabled="!hasChanges"
                @click="updateTopicInfo"
              >
                确认生效
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <div class="d-flex align-center justify-end mt-2">
            <v-btn color="primary" @click="showCreateCourseLessonPopup()">
              新增章节
            </v-btn>
        </div>
        <v-list two-line>
          <template v-for="(lesson, index) in lessons">
            <v-list-item :key="lesson.lessonId">
              <v-list-item-avatar tile size="120">
                <!-- 影片缩图，点击打开播放 -->
                <v-img
                  :src="lesson.thumbnailUrl"
                  @click="playVideo(lesson.videoFileUrl)"
                  class="cursor-pointer"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-icon>mdi-video</v-icon>
                    </v-row>
                  </template>
                  <!-- 播放按钮覆盖层 -->
                  <div class="video-overlay d-flex align-center justify-center">
                    <v-icon large>mdi-play-circle</v-icon>
                  </div>
                </v-img>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="mr-2 grey--text">#{{ index + 1 }}</span>
                  <span>{{ lesson.lessonName }}</span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="showLessonDetail(lesson)"
                  >
                    <v-icon left small>mdi-information</v-icon>
                    详情
                  </v-btn>
                  <v-btn
                    small
                    text
                    color="error"
                    @click="confirmDeleteLesson(lesson)"
                  >
                    <v-icon left small>mdi-delete</v-icon>
                    删除
                  </v-btn>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider
              v-if="index < lessons.length - 1"
              :key="`divider-${lesson.lessonId}`"
            ></v-divider>
          </template>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="close">关闭</v-btn>
      </v-card-actions>
    </v-card>

    <!-- 视频播放对话框 -->
    <v-dialog v-model="videoDialog" max-width="800">
      <v-card>
        <v-card-title class="headline">
          视频播放
          <v-spacer></v-spacer>
          <v-btn icon @click="videoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <video
            v-if="currentVideoUrl"
            :src="currentVideoUrl"
            controls
            style="width: 100%"
          ></video>
        </v-card-text>
      </v-card>
    </v-dialog>

    <create-course-lesson-popup
      ref="createCourseLessonPopup"
      @lesson-created="handleLessonCreate">
    </create-course-lesson-popup>

    <!-- 课程详情弹窗 -->
    <lesson-detail-popup
      ref="lessonDetailPopup"
      :item="selectedLesson"
      @lesson-updated="handleLessonUpdated"
    ></lesson-detail-popup>

    <delete-course-lesson-popup
      ref="deleteCourseLessonPopup"
      :item="selectedLesson"
      @lesson-deleted="handleLessonDeleted"
    ></delete-course-lesson-popup>
  </v-dialog>
</template>

<script>
import { courseApi } from '@/api/course'
import LessonDetailPopup from './LessonDetailPopup.vue'
import CreateCourseLessonPopup from './CreateCourseLessonPopup.vue'
import DeleteCourseLessonPopup from './DeleteCourseLessonPopup.vue'

export default {
  name: 'CourseTopicDetailPopup',
  components: {
    LessonDetailPopup, CreateCourseLessonPopup, DeleteCourseLessonPopup
  },
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialog: false,
      isEditing: false,
      formData: {
        id: '',
        topicName: '',
      },
      topicId: '',
      lessons: [],
      videoDialog: false,
      currentVideoUrl: null,
      selectedLesson: null,
      isEditingName: false,
      isEditingDescription: false,
      editingTopicName: '',
      editingDescription: '',
      originalData: null,
      newCoverImage: null
    }
  },
  computed: {
    hasChanges() {
      if (!this.originalData) return false;
      return this.formData.topicName !== this.originalData.topicName ||
        this.formData.description !== this.originalData.description ||
        this.newCoverImage !== null;
    }
  },
  methods: {
    open(item) {
      this.dialog = true;
      this.formData = {
        id: item.courseTopicId,
        topicName: item.courseTopicName,
        description: item.description,
        coverImageUrl: item.coverImageUrl
      };
      this.originalData = { ...this.formData };
      this.fetchLessons();
    },
    close() {
      this.dialog = false
      this.isEditingName = false;
      this.isEditingDescription = false;
      this.videoDialog = false;
      this.currentVideoUrl = null;
    },
    showCreateCourseLessonPopup() {
      this.$refs.createCourseLessonPopup.open(this.formData.id);
    },
    startEditingName() {
      this.isEditingName = true;
      this.editingTopicName = this.formData.topicName;
    },
    async saveTopicName() {
      if (!this.editingTopicName || this.editingTopicName.length > 50) return;
      this.formData.topicName = this.editingTopicName;
      this.isEditingName = false;
    },
    cancelEditingName() {
      this.editingTopicName = this.formData.topicName;
      this.isEditingName = false;
    },
    async fetchLessons() {
      try {
        const response = await courseApi.getTopicLessons(this.formData.id)
        this.lessons = response.data
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        this.$toast.error('获取课程列表失败：' + errorMessage);
      }
    },
    playVideo(videoUrl) {
      this.currentVideoUrl = videoUrl
      this.videoDialog = true
    },
    showLessonDetail(lesson) {
      this.selectedLesson = lesson;
      this.$refs.lessonDetailPopup.open(this.formData.id, lesson);
    },
    async confirmDeleteLesson(lesson) {
      this.selectedLesson = lesson;
      this.$refs.deleteCourseLessonPopup.open(lesson);
    },
    handleLessonUpdated() {
      console.log('lesson-updated has called');
      // const index = this.lessons.findIndex(l => l.id === updatedLesson.id)
      // if (index !== -1) {
      //   this.lessons.splice(index, 1, updatedLesson)
      // }
      this.fetchLessons();
    },
    startEditingDescription() {
      this.editingDescription = this.formData.description;
      this.isEditingDescription = true;
    },
    async saveDescription() {
      if (this.editingDescription.length >= 200) return;
      this.formData.description = this.editingDescription;
      this.isEditingDescription = false;
    },
    cancelEditingDescription() {
      this.editingDescription = this.formData.description;
      this.isEditingDescription = false;
    },
    triggerImageUpload() {
      this.$refs.imageInput.click();
    },
    handleImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.newCoverImage = file;
        // 预览新图片
        this.formData.coverImageUrl = URL.createObjectURL(file);
      }
    },
    handleLessonCreate() {
      console.log('新增章節成功，刷新列表');
      this.fetchLessons();
    },
    async updateTopicInfo() {
      try {
        const formData = new FormData();
        if (this.formData.topicName !== this.originalData.topicName) {
          formData.append('name', this.formData.topicName);
        } else {
          formData.append('name', this.originalData.topicName);
        }
        if (this.formData.description !== this.originalData.description) {
          formData.append('description', this.formData.description);
        } else {
          formData.append('description', this.originalData.description);
        }
        if (this.newCoverImage) {
          formData.append('coverImage', this.newCoverImage);
        }

        await courseApi.updateTopicInfo(this.formData.id, formData);
        
        // 更新 originalData，確保它完全匹配當前的 formData
        this.originalData = {
          id: this.formData.id,
          topicName: this.formData.topicName,
          description: this.formData.description,
          coverImageUrl: this.formData.coverImageUrl
        };
        
        // 清除新圖片標記
        this.newCoverImage = null;
        
        // 發出更新事件通知父組件
        this.$emit('update-course');
        
        this.$toast.success('主题信息更新成功');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        this.$toast.error('更新失败：' + errorMessage);
      }
    },
    
    handleLessonDeleted() {
      console.log('刪除章節成功，刷新列表');
      this.fetchLessons();
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s;
}

.v-img:hover .video-overlay {
  opacity: 1;
}

.v-list-item-avatar {
  margin-right: 16px;
}

.topic-cover {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.topic-cover:hover .cover-overlay {
  opacity: 1;
}
</style> 